import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';

import { ConciergeApiService } from './concierge-api.service';
import { ConciergeQueriesService } from './concierge-queries.service';
import { OnboardingDummyDataService } from './onboarding-dummy-data.service';
import { Addresses } from 'src/providers/customer/addresses';
import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

export interface UpsellTableRow {
  id: number;
  addressId: number | string;
  Property: {
    value: string;
    action: null;
    color: string;
    addressId: number;
  };
  Reservation: {
    value: string;
    reservationId: number;
    action: null;
  };
  Request: {
    value: string;
    action: null;
  };
  'last-column': {
    value: number;
    action: null;
    hasChevron: true;
  };
}

@Injectable({
  providedIn: 'root'
})
export class MarketingUpsellsService {
  private readonly conciergeApi = inject(ConciergeApiService);
  private readonly conciergeQueries = inject(ConciergeQueriesService);
  private readonly onboardingDummyDataService = inject(OnboardingDummyDataService);
  private readonly addresses = inject(Addresses);
  private readonly guestReservations = inject(GuestReservations);

  private propertiesCache: { [key: number]: string } = {};
  private reservationsCache: { [key: number]: string } = {};

  async fetchUpsells(isOnboarding: boolean): Promise<{ upsells: any[], total: string }> {
    if (isOnboarding) {
      const mockData = this.onboardingDummyDataService.getUpsellsMockData();
      return {
        upsells: mockData,
        total: mockData.length.toString()
      };
    }

    this.conciergeQueries.setUpsellsPage(1);
    await this.conciergeQueries.upsellsQuery.refetch();

    return {
      upsells: this.conciergeQueries.upsells(),
      total: this.conciergeQueries.totalUpsells()
    };
  }

  async fetchPaginatedUpsells(pageIndex: number, pageSize: number): Promise<any> {
    const itemsResponse = await lastValueFrom(
      this.conciergeApi.getConciergeItems(
        `&concierge_item_type_key=early_checkin,late_checkout,midstay_cleaning&page=${pageIndex}&per_page=${pageSize}&is_marked_done=false`
      )
    );
    return itemsResponse.body;
  }

  async buildRows(items: any[], addressResponse: any, isOnboarding: boolean): Promise<UpsellTableRow[]> {
    const limitedItems = items.length > 50 ? items.slice(0, 50) : items;
    const rows: UpsellTableRow[] = [];

    for (const item of limitedItems) {
      const component = item.components?.[0]?.value;

      let propertyValue = 'N/A';
      let reservationValue = 'N/A';
      let requestTypeValue = 'N/A';
      let addressId: any = 'N/A';
      let reservationId: any = 'N/A';

      if (item.metadata?.request_data?.body) {
        try {
          addressId = Number(item.metadata.request_data.body.match(/property:\s*(\d+)/)?.[1]);
          reservationId = Number(item.metadata.request_data.body.match(/reservation:\s*(\d+)/)?.[1]);

          const bodyData = await this.parseMetadataBody(item.metadata.request_data.body, isOnboarding);
          propertyValue = bodyData.property || 'N/A';
          reservationValue = bodyData.reservation || 'N/A';
          requestTypeValue = bodyData.requestType ? this.humanizeRequestType(bodyData.requestType) : 'N/A';
        } catch (_err) {
          // Intentionally empty
        }
      }

      rows.push({
        id: item.id,
        addressId,
        'Property': {
          value: propertyValue,
          action: null,
          color: addressResponse?.find((address: any) => address.id === addressId)?.color || '#1E2125',
          addressId
        },
        'Reservation': {
          value: reservationValue,
          reservationId,
          action: null
        },
        'Request': {
          value: requestTypeValue,
          action: null
        },
        'last-column': {
          value: item.id,
          action: null,
          hasChevron: true
        }
      });
    }

    return rows;
  }

  private async parseMetadataBody(
    bodyString: string,
    isOnboarding: boolean
  ): Promise<{ property?: string; reservation?: string; requestType?: string }> {
    try {
      const result: { property?: string; reservation?: string; requestType?: string } = {};

      const propertyMatch = bodyString.match(/property:\s*(\d+)/);
      if (propertyMatch) {
        const propertyId = parseInt(propertyMatch[1]);
        result.property = await this.getPropertyName(propertyId, isOnboarding);
      }

      const reservationMatch = bodyString.match(/reservation:\s*(\d+)/);
      if (reservationMatch) {
        const reservationId = parseInt(reservationMatch[1]);
        result.reservation = await this.getReservationDates(reservationId, isOnboarding);
      }

      const requestTypeMatch = bodyString.match(/requestType:\s*(\w+)/);
      if (requestTypeMatch) {
        result.requestType = requestTypeMatch[1];
      }

      return result;
    } catch (_err) {
      return {};
    }
  }

  private humanizeRequestType(requestType: string): string {
    const requestTypeMap: { [key: string]: string } = {
      'early_checkin': 'Early Check-in',
      'late_checkout': 'Late Check-out',
      'cleaning': 'Cleaning',
      'maintenance': 'Maintenance',
      'concierge': 'Concierge Service',
      'upsell': 'Upsell'
    };

    return requestTypeMap[requestType] || requestType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  private async getPropertyName(propertyId: number, isOnboarding: boolean): Promise<string> {
    if (this.propertiesCache[propertyId]) {
      return this.propertiesCache[propertyId];
    }

    if (isOnboarding) {
      const mockPropertyNames = [
        'Beach House', 'Mountain Cabin', 'City Apartment', 'Lake House', 'Downtown Loft',
        'Ocean View Villa', 'Forest Retreat', 'Urban Penthouse', 'Riverside Cottage', 'Hillside Manor',
        'Coastal Bungalow', 'Alpine Chalet', 'Metropolitan Suite', 'Garden Estate', 'Valley View Lodge'
      ];
      const mockName = mockPropertyNames[propertyId % mockPropertyNames.length] || `Property #${propertyId}`;
      this.propertiesCache[propertyId] = mockName;
      return mockName;
    }

    try {
      const property = await this.addresses.getAddress(propertyId);
      const propertyName = property.address_name || `Property #${propertyId}`;
      this.propertiesCache[propertyId] = propertyName;
      return propertyName;
    } catch (_err) {
      const fallbackName = `Property #${propertyId}`;
      this.propertiesCache[propertyId] = fallbackName;
      return fallbackName;
    }
  }

  private async getReservationDates(reservationId: number, isOnboarding: boolean): Promise<string> {
    if (this.reservationsCache[reservationId]) {
      return this.reservationsCache[reservationId];
    }

    if (isOnboarding) {
      const currentYear = new Date().getFullYear();
      const mockDates = [
        `Jan 15-20, ${currentYear}`,
        `Feb 5-10, ${currentYear}`,
        `Mar 8-13, ${currentYear}`,
        `Apr 1-6, ${currentYear}`,
        `May 10-15, ${currentYear}`,
        `Jun 5-12, ${currentYear}`,
        `Jul 10-15, ${currentYear}`,
        `Aug 1-7, ${currentYear}`,
        `Sep 20-25, ${currentYear}`,
        `Oct 3-8, ${currentYear}`,
        `Nov 12-17, ${currentYear}`,
        `Dec 20-27, ${currentYear}`
      ];
      const mockDate = mockDates[reservationId % mockDates.length] || `Reservation #${reservationId}`;
      this.reservationsCache[reservationId] = mockDate;
      return mockDate;
    }

    try {
      const reservation = await this.guestReservations.getReservationById(reservationId);
      const reservationDates = this.getReservationViewValue(reservation);
      this.reservationsCache[reservationId] = reservationDates;
      return reservationDates;
    } catch (_err) {
      const fallbackDates = `Reservation #${reservationId}`;
      this.reservationsCache[reservationId] = fallbackDates;
      return fallbackDates;
    }
  }

  private getReservationViewValue(reservation: any): string {
    const checkInDate = reservation?.source_data?.guest_reservation?.check_in_date || reservation?.source_data?.check_in_date;
    const checkOutDate = reservation?.source_data?.guest_reservation?.check_out_date || reservation?.source_data?.check_out_date;
    const datePipe = new ParseDateTimePipe();

    return `${datePipe.transform(checkInDate, 'MMM d')} ${datePipe.transform(checkInDate, 'h:mma')} - ${datePipe.transform(checkOutDate, 'MMM d')} ${datePipe.transform(checkOutDate, 'h:mma')}`;
  }

  getRequestTypeKeyFromDisplay(displayValue: string): string {
    const requestTypeMap: { [key: string]: string } = {
      'Early Check-in': 'early_checkin',
      'Early Check-In': 'early_checkin',
      'Late Check-out': 'late_checkout',
      'Late Check-Out': 'late_checkout',
      'Midstay Cleaning': 'midstay_cleaning'
    };

    return requestTypeMap[displayValue] || 'early_checkin';
  }

  invalidateCaches(): void {
    this.propertiesCache = {};
    this.reservationsCache = {};
  }
}
